import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAdvertiser } from "./useAdvertiser";
import BotCheck, { useBotCheck } from "@/components/lux/BotCheck";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";

export default function RegisterPage() {
  useSeo({ title: "Register as an advertiser | LUX", ...PRIVATE_SEO });
  const navigate = useNavigate();
  const { refetchSession } = useAdvertiser();
  const bot = useBotCheck();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm18, setConfirm18] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!confirm18) return setError("Please confirm you are 18 or over");
    if (!acceptTerms) return setError("Please accept the Terms and Posting guidelines");
    if (!bot.answer) return setError("Answer the human check");
    setBusy(true);
    try {
      await api.post("/auth/register", { email, password, confirm_18: confirm18, accept_terms: acceptTerms, ...bot.payload() });
      toast.success("Account created. Set up two-factor authentication next.");
      await refetchSession();
      navigate("/advertiser/2fa?mode=setup", { replace: true });
    } catch (err) {
      setError(errMsg(err));
      bot.refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="lux-card p-6 sm:p-8" data-testid="register-page">
      <p className="lux-label">For advertisers</p>
      <h1 className="mt-3 font-serif text-3xl text-lux-text">Create your account</h1>
      <p className="mt-2 text-sm text-lux-muted">
        LUX uses password + authenticator-app 2FA. Have your authenticator ready on your phone before you continue.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-5" noValidate>
        <div>
          <label htmlFor="reg-email" className="lux-label">Email</label>
          <input id="reg-email" type="email" required autoComplete="email" value={email}
            onChange={(e) => setEmail(e.target.value)} className="lux-input mt-2"
            placeholder="name@example.com" data-testid="register-email-input" />
        </div>
        <div>
          <label htmlFor="reg-pw" className="lux-label">Password</label>
          <div className="relative mt-2">
            <input id="reg-pw" type={showPw ? "text" : "password"} required autoComplete="new-password" value={password}
              onChange={(e) => setPassword(e.target.value)} className="lux-input pr-11"
              placeholder="At least 10 characters, mix letters & numbers" data-testid="register-password-input" />
            <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-2 text-lux-silver hover:text-lux-silverBright"
              data-testid="register-password-toggle">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1 text-xs text-lux-muted">10+ characters. Mix letters and numbers.</p>
        </div>
        <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-lux-border bg-lux-surface2 p-3 text-sm text-lux-text/90">
          <input type="checkbox" checked={confirm18} onChange={(e) => setConfirm18(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#C0C0C8]" data-testid="register-age-checkbox" />
          <span>I confirm I am 18 or over and will complete identity verification.</span>
        </label>
        <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-lux-border bg-lux-surface2 p-3 text-sm text-lux-text/90">
          <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#C0C0C8]" data-testid="register-terms-checkbox" />
          <span>I accept the <Link to="/terms" target="_blank" className="lux-link">Terms</Link> and the <Link to="/posting-guidelines" target="_blank" className="lux-link">Posting guidelines</Link>.</span>
        </label>
        <BotCheck state={bot} testPrefix="register-form" />
        {error && <p role="alert" className="text-sm text-lux-error" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={busy} className="lux-btn lux-btn-primary w-full" data-testid="register-submit-button">
          {busy ? "Creating account..." : "Create account"}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-lux-muted">
        Already have an account? <Link to="/advertiser/login" className="lux-link" data-testid="register-login-link">Sign in</Link>
      </p>
    </section>
  );
}
